import type { Rect, Vec } from './types.js';

export interface SnapGuide {
  /** 'x' guides are vertical lines, 'y' guides are horizontal. */
  axis: 'x' | 'y';
  /** Board coordinate of the line. */
  at: number;
  /** Extent of the guide, so it can be drawn just across the involved shapes. */
  from: number;
  to: number;
  /** Set for equal-spacing guides, which render with distance badges. */
  gap?: number;
}

export interface SnapResult {
  delta: Vec;
  guides: SnapGuide[];
}

interface Candidate {
  value: number;
  /** Perpendicular span of the source rect, used to size the guide. */
  lo: number;
  hi: number;
}

const NO_SNAP: SnapResult = { delta: { x: 0, y: 0 }, guides: [] };

function edges(r: Rect, axis: 'x' | 'y'): [number, number, number] {
  return axis === 'x' ? [r.x, r.x + r.w / 2, r.x + r.w] : [r.y, r.y + r.h / 2, r.y + r.h];
}

function span(r: Rect, axis: 'x' | 'y'): [number, number] {
  return axis === 'x' ? [r.y, r.y + r.h] : [r.x, r.x + r.w];
}

/**
 * Edge and centre alignment against nearby objects, plus optional grid snap.
 *
 * `threshold` is in board units, so callers divide their pixel threshold by the
 * current zoom: snapping should feel like a fixed number of screen pixels no
 * matter how far the user has zoomed out.
 */
export function computeSnap(
  moving: Rect,
  others: readonly Rect[],
  threshold: number,
  grid?: { size: number; enabled: boolean },
): SnapResult {
  if (others.length === 0 && !grid?.enabled) return NO_SNAP;

  const guides: SnapGuide[] = [];
  const delta: Vec = { x: 0, y: 0 };

  for (const axis of ['x', 'y'] as const) {
    const movingEdges = edges(moving, axis);
    const [mLo, mHi] = span(moving, axis);

    let best: { diff: number; guide: SnapGuide } | null = null;

    for (const other of others) {
      const otherEdges = edges(other, axis);
      const [oLo, oHi] = span(other, axis);

      for (const me of movingEdges) {
        for (const oe of otherEdges) {
          const diff = oe - me;
          if (Math.abs(diff) > threshold) continue;
          if (best && Math.abs(diff) >= Math.abs(best.diff)) continue;
          best = {
            diff,
            guide: {
              axis,
              at: oe,
              from: Math.min(mLo, oLo),
              to: Math.max(mHi, oHi),
            },
          };
        }
      }
    }

    if (best) {
      delta[axis] = best.diff;
      guides.push(best.guide);
    } else if (grid?.enabled && grid.size > 0) {
      const origin = axis === 'x' ? moving.x : moving.y;
      const snapped = Math.round(origin / grid.size) * grid.size;
      if (Math.abs(snapped - origin) <= threshold) delta[axis] = snapped - origin;
    }
  }

  return { delta, guides };
}

/**
 * Equal-spacing guides: when the moving rect sits between two others, offer the
 * position that makes both gaps identical. This is the behaviour people notice
 * most when arranging sticky notes in a row.
 */
export function computeSpacingSnap(
  moving: Rect,
  others: readonly Rect[],
  threshold: number,
): SnapResult {
  const guides: SnapGuide[] = [];
  const delta: Vec = { x: 0, y: 0 };

  for (const axis of ['x', 'y'] as const) {
    const size = axis === 'x' ? moving.w : moving.h;
    const start = (r: Rect) => (axis === 'x' ? r.x : r.y);
    const end = (r: Rect) => (axis === 'x' ? r.x + r.w : r.y + r.h);

    // Only consider neighbours that overlap on the other axis.
    const [mLo, mHi] = span(moving, axis);
    const row = others.filter((o) => {
      const [oLo, oHi] = span(o, axis);
      return oHi > mLo && oLo < mHi;
    });
    if (row.length < 2) continue;

    const sorted = [...row].sort((a, b) => start(a) - start(b));
    let best: { diff: number; gap: number; a: Rect; b: Rect } | null = null;

    for (let i = 0; i < sorted.length - 1; i++) {
      const left = sorted[i]!;
      const right = sorted[i + 1]!;
      const free = start(right) - end(left);
      if (free < size) continue;
      const gap = (free - size) / 2;
      const target = end(left) + gap;
      const diff = target - start(moving);
      if (Math.abs(diff) > threshold) continue;
      if (best && Math.abs(diff) >= Math.abs(best.diff)) continue;
      best = { diff, gap, a: left, b: right };
    }

    if (best) {
      delta[axis] = best.diff;
      const at = axis === 'x' ? moving.y + moving.h / 2 : moving.x + moving.w / 2;
      guides.push({
        axis: axis === 'x' ? 'y' : 'x',
        at,
        from: end(best.a),
        to: start(best.b),
        gap: best.gap,
      });
    }
  }

  return { delta, guides };
}

/** Combines alignment and spacing snapping, preferring alignment per axis. */
export function snapMove(
  moving: Rect,
  others: readonly Rect[],
  threshold: number,
  grid?: { size: number; enabled: boolean },
): SnapResult {
  const align = computeSnap(moving, others, threshold, grid);
  const spacing = computeSpacingSnap(moving, others, threshold);

  const delta: Vec = { x: align.delta.x, y: align.delta.y };
  const guides = [...align.guides];

  if (delta.x === 0 && spacing.delta.x !== 0) {
    delta.x = spacing.delta.x;
    guides.push(...spacing.guides.filter((g) => g.axis === 'y'));
  }
  if (delta.y === 0 && spacing.delta.y !== 0) {
    delta.y = spacing.delta.y;
    guides.push(...spacing.guides.filter((g) => g.axis === 'x'));
  }

  return { delta, guides };
}

/** Snap a rotation to 15-degree increments while a modifier is held. */
export function snapAngle(angle: number, stepDegrees = 15): number {
  const step = (stepDegrees * Math.PI) / 180;
  return Math.round(angle / step) * step;
}

export function snapPointToGrid(p: Vec, size: number): Vec {
  if (size <= 0) return p;
  return { x: Math.round(p.x / size) * size, y: Math.round(p.y / size) * size };
}
